const DEFAULT_ZONE_OUTCOME_OPTIONS = {
  approachPips: 10,
  spikeMovePips: 10,
  failureBufferPips: 15,
  spikeWindowMs: 60000,
};

const trackerStore = new Map();

export const ZONE_OUTCOME_STATES = {
  FRESH: "FRESH",
  APPROACHING: "APPROACHING",
  ACTIVE: "ACTIVE",
  RESPECTED: "RESPECTED",
  FAILED: "FAILED",
};

function buildTrackerKey(symbol, timeframe) {
  return `${symbol || "unknown"}:${timeframe || "unknown"}`;
}

function normalizeZoneType(zone) {
  return String(zone?.type || "").toUpperCase();
}

function isDemandZone(zone) {
  return normalizeZoneType(zone) === "DEMAND";
}

function getZoneDirection(zone) {
  return isDemandZone(zone) ? "BUY" : "SELL";
}

function getObservedAt(latestCandle) {
  if (latestCandle?.isClosed === false) {
    return Date.now();
  }

  if (latestCandle?.time != null) {
    return Number(latestCandle.time) * 1000;
  }

  return Date.now();
}

function buildZoneSignature(zone, analysis) {
  return [
    zone?.id || "NO_ZONE",
    zone?.high ?? "NO_HIGH",
    zone?.low ?? "NO_LOW",
    analysis?.bosState?.bosCandleTime ?? analysis?.structure?.bosCandleTime ?? "NO_BOS_TIME",
  ].join(":");
}

function isPriceInsideZone(zone, price) {
  if (!zone || price == null) {
    return false;
  }

  const low = Number(zone.low);
  const high = Number(zone.high);
  const normalizedPrice = Number(price);

  if (!Number.isFinite(low) || !Number.isFinite(high) || !Number.isFinite(normalizedPrice)) {
    return false;
  }

  return normalizedPrice >= low && normalizedPrice <= high;
}

function doesCandleTouchZone(zone, candle) {
  if (!zone || !candle) {
    return false;
  }

  const low = Number(zone.low);
  const high = Number(zone.high);
  const candleLow = Number(candle.low);
  const candleHigh = Number(candle.high);

  if (
    !Number.isFinite(low) ||
    !Number.isFinite(high) ||
    !Number.isFinite(candleLow) ||
    !Number.isFinite(candleHigh)
  ) {
    return false;
  }

  return candleLow <= high && candleHigh >= low;
}

function clampPriceToZone(zone, price) {
  if (!zone || price == null) {
    return null;
  }

  const low = Number(zone.low);
  const high = Number(zone.high);
  const normalizedPrice = Number(price);

  if (!Number.isFinite(low) || !Number.isFinite(high) || !Number.isFinite(normalizedPrice)) {
    return null;
  }

  return Math.min(high, Math.max(low, normalizedPrice));
}

function doesCandleCloseOutsideZone(zone, candle) {
  if (!zone || !candle) {
    return false;
  }

  const close = Number(candle.close);

  if (!Number.isFinite(close)) {
    return false;
  }

  return isDemandZone(zone) ? close > Number(zone.high) : close < Number(zone.low);
}

function getCandleBodySize(candle) {
  if (!candle) {
    return 0;
  }

  const open = Number(candle.open);
  const close = Number(candle.close);

  if (!Number.isFinite(open) || !Number.isFinite(close)) {
    return 0;
  }

  return Math.abs(close - open);
}

function getCandleRangeSize(candle) {
  if (!candle) {
    return 0;
  }

  const low = Number(candle.low);
  const high = Number(candle.high);

  if (!Number.isFinite(low) || !Number.isFinite(high)) {
    return 0;
  }

  return Math.abs(high - low);
}

function getSameCandleSpikeMetrics(zone, candle) {
  if (!zone || !candle) {
    return {
      closesOutsideZone: false,
      bodySize: 0,
      rangeSize: 0,
      closeMove: 0,
      strength: 0,
    };
  }

  const closesOutsideZone = doesCandleCloseOutsideZone(zone, candle);
  const bodySize = getCandleBodySize(candle);
  const rangeSize = getCandleRangeSize(candle);
  const closeMove = Math.max(0, measureMoveAwayFromZone(zone, Number(candle.close)));
  const strength = Math.max(closeMove, bodySize, rangeSize);

  return {
    closesOutsideZone,
    bodySize,
    rangeSize,
    closeMove,
    strength,
  };
}

function isSameCandleSpike(zone, candle, spikeMovePips) {
  const metrics = getSameCandleSpikeMetrics(zone, candle);

  return {
    ...metrics,
    detected: doesCandleTouchZone(zone, candle) &&
      metrics.closesOutsideZone &&
      metrics.strength >= spikeMovePips,
  };
}

function isApproachingZone(zone, price, approachPips) {
  if (!zone || price == null) {
    return false;
  }

  const low = Number(zone.low);
  const high = Number(zone.high);
  const normalizedPrice = Number(price);

  if (!Number.isFinite(low) || !Number.isFinite(high) || !Number.isFinite(normalizedPrice)) {
    return false;
  }

  if (isDemandZone(zone)) {
    return normalizedPrice > high && normalizedPrice <= high + approachPips;
  }

  return normalizedPrice < low && normalizedPrice >= low - approachPips;
}

function isZoneFailure(zone, price, failureBufferPips) {
  if (!zone || price == null) {
    return false;
  }

  const normalizedPrice = Number(price);

  if (!Number.isFinite(normalizedPrice)) {
    return false;
  }

  if (isDemandZone(zone)) {
    return normalizedPrice <= Number(zone.low) - failureBufferPips;
  }

  return normalizedPrice >= Number(zone.high) + failureBufferPips;
}

function measureMoveAwayFromZone(zone, price) {
  if (!zone || price == null) {
    return 0;
  }

  const normalizedPrice = Number(price);

  if (!Number.isFinite(normalizedPrice)) {
    return 0;
  }

  return isDemandZone(zone)
    ? normalizedPrice - Number(zone.high)
    : Number(zone.low) - normalizedPrice;
}

function getOutcomeLabel(state, zone) {
  const zoneSide = isDemandZone(zone) ? "Demand" : "Supply";

  if (state === ZONE_OUTCOME_STATES.APPROACHING) {
    return `Approaching ${zoneSide}`;
  }

  if (state === ZONE_OUTCOME_STATES.ACTIVE) {
    return `Active ${zoneSide}`;
  }

  if (state === ZONE_OUTCOME_STATES.RESPECTED) {
    return `Respected ${zoneSide}`;
  }

  if (state === ZONE_OUTCOME_STATES.FAILED) {
    return `Failed ${zoneSide}`;
  }

  return `Fresh ${zoneSide}`;
}

function buildBaseAlert({
  tracker,
  eventType,
  price,
  observedAt,
  moveSize = null,
  message = "",
}) {
  return {
    id: `${eventType}:${tracker.zone.id}:${observedAt}`,
    eventType,
    symbol: tracker.symbol,
    timeframe: tracker.timeframe,
    market: tracker.market,
    side: getZoneDirection(tracker.zone),
    price,
    moveSize,
    message,
    zone: {
      id: tracker.zone.id,
      type: normalizeZoneType(tracker.zone).toLowerCase(),
      status: tracker.state.toLowerCase(),
      // tracker.zone.range.{high,low} is a redundant copy set by bosStateEngine.js's
      // updateBOSState() alongside the flat high/low - fall back to it so a consumer reading
      // price_high/price_low never gets undefined as long as either copy survived.
      price_high: tracker.zone.high ?? tracker.zone.range?.high,
      price_low: tracker.zone.low ?? tracker.zone.range?.low,
    },
    candleTime: tracker.latestCandleTime ?? null,
    createdAt: new Date(observedAt).toISOString(),
    source: "zone_outcome_tracker",
  };
}

function buildResult({
  tracker,
  type,
  observedAt,
  moveSize,
  price,
  analysis,
  options,
}) {
  const direction = getZoneDirection(tracker.zone);
  const tradeKey = [
    "zone-outcome",
    tracker.symbol,
    tracker.timeframe,
    tracker.zone.id,
    tracker.entryTime || observedAt,
    type,
  ].join(":");
  const stopLoss = isDemandZone(tracker.zone)
    ? Number(tracker.zone.low) - options.failureBufferPips
    : Number(tracker.zone.high) + options.failureBufferPips;

  return {
    id: tradeKey,
    type,
    tradeKey,
    market: tracker.market,
    direction,
    zoneId: tracker.zone.id,
    zoneType: normalizeZoneType(tracker.zone),
    zoneHigh: tracker.zone.high,
    zoneLow: tracker.zone.low,
    entryTime: tracker.entryTime || observedAt,
    entryPrice: tracker.entryPrice,
    resultTime: observedAt,
    resultPrice: price,
    moveSize,
    timeframe: tracker.timeframe,
    symbol: tracker.symbol,
    stopLoss,
    takeProfit: type === "spike" ? price : null,
    result: type === "spike" ? "SPIKE" : "FAIL",
    bosPrice: analysis?.bosState?.bosPrice ?? analysis?.structure?.bosPrice ?? null,
    bosIndex: analysis?.bosState?.bosIndex ?? analysis?.structure?.bosCandleIndex ?? null,
    bosTime: analysis?.bosState?.bosCandleTime ?? analysis?.structure?.bosCandleTime ?? null,
    signalTimestamp: tracker.entryTime || observedAt,
    evaluatedAt: observedAt,
    persisted: false,
    persistenceStatus: "pending",
  };
}

function createTracker({ symbol, timeframe, market, zone, analysis }) {
  return {
    symbol,
    timeframe,
    market,
    zone,
    zoneSignature: buildZoneSignature(zone, analysis),
    lockedBosCandleTime:
      analysis?.bosState?.bosCandleTime ?? analysis?.structure?.bosCandleTime ?? null,
    state: ZONE_OUTCOME_STATES.FRESH,
    result: null,
    lastEvent: null,
    moveSize: 0,
    entryTime: null,
    entryPrice: null,
    latestCandleTime: null,
    emittedEventKeys: new Set(),
  };
}

function cloneZoneWithOutcome(zone, zoneOutcome) {
  if (!zone) {
    return zone;
  }

  const nextState =
    zoneOutcome?.state === ZONE_OUTCOME_STATES.FAILED
      ? "failed"
      : zoneOutcome?.state === ZONE_OUTCOME_STATES.RESPECTED
      ? "tested"
      : zone.state;

  return {
    ...zone,
    label: zoneOutcome?.label || zone.label,
    state: nextState,
    failed: zoneOutcome?.state === ZONE_OUTCOME_STATES.FAILED || zone.failed === true,
    tested: zoneOutcome?.state === ZONE_OUTCOME_STATES.RESPECTED || zone.tested === true,
    outcomeState: zoneOutcome?.state || null,
    outcomeResult: zoneOutcome?.result?.result || null,
  };
}

function emitTrackerEvent(tracker, alert) {
  if (!alert || tracker.emittedEventKeys.has(alert.id)) {
    return;
  }

  tracker.emittedEventKeys.add(alert.id);
  tracker.lastEvent = alert;
}

function buildZoneOutcomeSnapshot(tracker) {
  if (!tracker) {
    return null;
  }

  return {
    zoneId: tracker.zone?.id || null,
    state: tracker.state,
    label: getOutcomeLabel(tracker.state, tracker.zone),
    result: tracker.result,
    lastEvent: tracker.lastEvent,
    moveSize: tracker.moveSize,
    candleTime: tracker.latestCandleTime,
    entryTime: tracker.entryTime,
    entryPrice: tracker.entryPrice,
    phases: Object.values(ZONE_OUTCOME_STATES),
  };
}

export function resetZoneOutcomeTracking({ symbol, timeframe }) {
  trackerStore.delete(buildTrackerKey(symbol, timeframe));
}

export function getPendingTrackedZone({ symbol, timeframe }) {
  const tracker = trackerStore.get(buildTrackerKey(symbol, timeframe)) || null;

  if (!tracker) {
    return null;
  }

  if (
    tracker.state === ZONE_OUTCOME_STATES.RESPECTED ||
    tracker.state === ZONE_OUTCOME_STATES.FAILED
  ) {
    return null;
  }

  return {
    ...tracker.zone,
    type: normalizeZoneType(tracker.zone),
    outcomeState: tracker.state,
    outcomeResult: tracker.result?.result || null,
    lockedBosCandleTime: tracker.lockedBosCandleTime ?? null,
  };
}

export function patchZoneOutcomeResult({ symbol, timeframe, resultId, patch }) {
  if (!resultId || !patch) {
    return null;
  }

  const tracker = trackerStore.get(buildTrackerKey(symbol, timeframe)) || null;

  if (!tracker?.result || tracker.result.id !== resultId) {
    return null;
  }

  tracker.result = {
    ...tracker.result,
    ...patch,
  };

  return tracker.result;
}

export function evaluateZoneOutcome({
  symbol,
  timeframe,
  market,
  candles,
  analysis,
  options = {},
}) {
  if (!analysis) {
    return null;
  }

  const resolvedOptions = {
    ...DEFAULT_ZONE_OUTCOME_OPTIONS,
    ...options,
    spikeWindowMs:
      options.spikeWindowMs ?? Math.max(5000, Number(timeframe || 0) * 1000 || 60000),
  };
  const trackerKey = buildTrackerKey(symbol, timeframe);
  const selectedZone = analysis?.bosState?.zone || analysis?.zoneState || null;
  const trackerCanRun =
    analysis?.bosState?.state === "LOCKED" &&
    selectedZone?.id &&
    selectedZone?.high != null &&
    selectedZone?.low != null;
  let tracker = trackerStore.get(trackerKey) || null;

  if (!trackerCanRun) {
    return buildZoneOutcomeSnapshot(tracker);
  }

  const zoneSignature = buildZoneSignature(selectedZone, analysis);

  if (!tracker || tracker.zoneSignature !== zoneSignature) {
    tracker = createTracker({
      symbol,
      timeframe,
      market,
      zone: {
        ...selectedZone,
        type: normalizeZoneType(selectedZone),
      },
      analysis,
    });
    trackerStore.set(trackerKey, tracker);
  } else {
    tracker.zone = {
      ...tracker.zone,
      ...selectedZone,
      type: normalizeZoneType(selectedZone),
    };
    tracker.market = market;
  }

  const latestCandle = candles?.[candles.length - 1] || null;
  const latestPrice = Number(latestCandle?.close);

  tracker.latestCandleTime = latestCandle?.time ?? null;

  if (!latestCandle || latestCandle.isClosed !== false || !Number.isFinite(latestPrice)) {
    return buildZoneOutcomeSnapshot(tracker);
  }

  if (
    tracker.state === ZONE_OUTCOME_STATES.RESPECTED ||
    tracker.state === ZONE_OUTCOME_STATES.FAILED
  ) {
    return buildZoneOutcomeSnapshot(tracker);
  }

  const observedAt = getObservedAt(latestCandle);
  const candleTouchedZone = doesCandleTouchZone(tracker.zone, latestCandle);
  const sameCandleSpike = isSameCandleSpike(
    tracker.zone,
    latestCandle,
    resolvedOptions.spikeMovePips
  );

  if (isPriceInsideZone(tracker.zone, latestPrice) || candleTouchedZone) {
    if (tracker.state !== ZONE_OUTCOME_STATES.ACTIVE) {
      tracker.state = ZONE_OUTCOME_STATES.ACTIVE;
      tracker.entryTime = tracker.entryTime || observedAt;
      emitTrackerEvent(
        tracker,
        buildBaseAlert({
          tracker,
          eventType: "market_in_zone",
          price: latestPrice,
          observedAt,
          message: "Zone active - observe",
        })
      );
    }

    // Entry price is the deepest wick extremity reached into the zone before the outcome
    // resolves - the candle's high for a supply/sell zone (the rejection point), the low for a
    // demand/buy zone - not the tick that merely first grazed the zone boundary. Ticks build the
    // active candle's wick up in real time, so the first touching tick is almost always right at
    // the boundary; keep tracking the running extreme on every tick while the zone stays active
    // so this lands on the actual peak/trough the price reached, not just where it first entered.
    const wickExtreme = isDemandZone(tracker.zone) ? latestCandle?.low : latestCandle?.high;
    const candidateEntry =
      wickExtreme ?? clampPriceToZone(tracker.zone, latestPrice) ?? latestPrice;

    tracker.entryPrice =
      tracker.entryPrice == null
        ? candidateEntry
        : isDemandZone(tracker.zone)
        ? Math.min(tracker.entryPrice, candidateEntry)
        : Math.max(tracker.entryPrice, candidateEntry);
  } else if (
    tracker.state === ZONE_OUTCOME_STATES.FRESH &&
    isApproachingZone(tracker.zone, latestPrice, resolvedOptions.approachPips)
  ) {
    tracker.state = ZONE_OUTCOME_STATES.APPROACHING;
    emitTrackerEvent(
      tracker,
      buildBaseAlert({
        tracker,
        eventType: "zone_approach",
        price: latestPrice,
        observedAt,
        message: "Price approaching zone",
      })
    );
  }

  if (tracker.state === ZONE_OUTCOME_STATES.ACTIVE) {
    tracker.moveSize = Number(
      Math.max(
        0,
        measureMoveAwayFromZone(tracker.zone, latestPrice),
        sameCandleSpike.detected ? sameCandleSpike.strength : 0
      ).toFixed(2)
    );

    if (isZoneFailure(tracker.zone, latestPrice, resolvedOptions.failureBufferPips)) {
      tracker.state = ZONE_OUTCOME_STATES.FAILED;
      tracker.result = buildResult({
        tracker,
        type: "fail",
        observedAt,
        moveSize: tracker.moveSize,
        price: latestPrice,
        analysis,
        options: resolvedOptions,
      });
      emitTrackerEvent(
        tracker,
        buildBaseAlert({
          tracker,
          eventType: "zone_failed",
          price: latestPrice,
          observedAt,
          moveSize: tracker.moveSize,
          message: "Zone failed (SL hit)",
        })
      );
    } else {
      if (sameCandleSpike.detected) {
        tracker.state = ZONE_OUTCOME_STATES.RESPECTED;
        tracker.result = buildResult({
          tracker,
          type: "spike",
          observedAt,
          moveSize: tracker.moveSize,
          price: latestPrice,
          analysis,
          options: resolvedOptions,
        });
        emitTrackerEvent(
          tracker,
          buildBaseAlert({
            tracker,
            eventType: "spike_detected",
            price: latestPrice,
            observedAt,
            moveSize: tracker.moveSize,
            message: "Spike detected on first touch candle",
          })
        );

        return buildZoneOutcomeSnapshot(tracker);
      }

      const spikeWindowOpen =
        tracker.entryTime != null &&
        observedAt - tracker.entryTime <= resolvedOptions.spikeWindowMs;
      const spikeMoveReached = tracker.moveSize >= resolvedOptions.spikeMovePips;

      if (spikeWindowOpen && spikeMoveReached) {
        tracker.state = ZONE_OUTCOME_STATES.RESPECTED;
        tracker.result = buildResult({
          tracker,
          type: "spike",
          observedAt,
          moveSize: tracker.moveSize,
          price: latestPrice,
          analysis,
          options: resolvedOptions,
        });
        emitTrackerEvent(
          tracker,
          buildBaseAlert({
            tracker,
            eventType: "spike_detected",
            price: latestPrice,
            observedAt,
            moveSize: tracker.moveSize,
            message: "Spike detected",
          })
        );
      }
    }
  }

  return buildZoneOutcomeSnapshot(tracker);
}

export function withZoneOutcomeAnalysis(analysis, zoneOutcome) {
  if (!analysis) {
    return analysis;
  }

  if (!zoneOutcome) {
    return {
      ...analysis,
      zoneOutcome: null,
    };
  }

  const trackedZoneId = zoneOutcome.zoneId;
  const nextZoneState =
    analysis.zoneState?.id === trackedZoneId
      ? cloneZoneWithOutcome(analysis.zoneState, zoneOutcome)
      : analysis.zoneState;
  const nextBosZone =
    analysis.bosState?.zone?.id === trackedZoneId
      ? cloneZoneWithOutcome(analysis.bosState.zone, zoneOutcome)
      : analysis.bosState?.zone;
  const nextSignalState = deriveSignalStateFromZoneOutcome(analysis.signalState, zoneOutcome);
  return {
    ...analysis,
    // Keep chart zone collections driven by historical zone resolution only.
    // Live zone-outcome tracking should power alerts/panel state without
    // repainting every matching overlay zone as failed/tested in real time.
    zoneAnalysis: analysis.zoneAnalysis,
    zoneState: nextZoneState,
    bosState: analysis.bosState
      ? {
          ...analysis.bosState,
          zone: nextBosZone,
        }
      : analysis.bosState,
    signalState: nextSignalState,
    zoneOutcome,
  };
}

function deriveSignalStateFromZoneOutcome(signalState, zoneOutcome) {
  if (!zoneOutcome) {
    return signalState;
  }

  if (zoneOutcome.state === ZONE_OUTCOME_STATES.RESPECTED) {
    return {
      ...signalState,
      state: "SPIKE",
      signal: "ENTRY",
      direction: zoneOutcome.result?.direction || signalState?.direction || null,
      price: zoneOutcome.result?.resultPrice ?? signalState?.price ?? null,
      time: zoneOutcome.candleTime ?? signalState?.time ?? null,
      reason: zoneOutcome.lastEvent?.message || "Spike detected",
    };
  }

  if (zoneOutcome.state === ZONE_OUTCOME_STATES.FAILED) {
    return {
      ...signalState,
      state: "FAIL",
      reason: zoneOutcome.lastEvent?.message || "Zone failed",
    };
  }

  return signalState;
}
